import React, { createContext, useContext, useRef, useEffect, forwardRef } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import { Popper, useMediaQuery } from '@mui/material';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import PropTypes from 'prop-types';
import { VariableSizeList } from 'react-window';

const LISTBOX_PADDING = 8;
const ITEM_SIZE_DESKTOP = 44;
const ITEM_SIZE_MOBILE = 52;
const GROUP_SIZE = 48;
const MAX_VISIBLE_ITEMS = 7;

const OuterElementContext = createContext({});

const OuterElementType = forwardRef(function OuterElement(props, ref) {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

export function createListboxComponent(RowRenderer) {
  const Component = forwardRef(function VirtualizedListbox(props, ref) {
    const { children, ...other } = props;
    const itemData = [];
    children.forEach((item) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    });

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

    const itemCount = itemData.length;
    const itemSize = smUp ? ITEM_SIZE_DESKTOP : ITEM_SIZE_MOBILE;

    const getChildSize = (child) => {
      if (child.hasOwnProperty('group')) return GROUP_SIZE;
      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > MAX_VISIBLE_ITEMS) return MAX_VISIBLE_ITEMS * itemSize;
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={(index) => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
          >
            {RowRenderer}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  Component.propTypes = { children: PropTypes.node };
  return Component;
}

export const LISTBOX_PADDING_VALUE = LISTBOX_PADDING;

export const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    padding: '4px 0',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
  [`& .${autocompleteClasses.paper}`]: {
    borderRadius: 12,
    border: '1px solid',
    borderColor: '#e5e8ed',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.06), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
    marginTop: 4,
    overflow: 'hidden',
  },
  [`& .${autocompleteClasses.noOptions}`]: {
    fontSize: '0.875rem',
    color: '#6b7280',
    padding: '12px 16px',
  },
  [`& .${autocompleteClasses.loading}`]: {
    fontSize: '0.875rem',
    color: '#9ca3ae',
    padding: '16px',
  },
});
